import { useState, useCallback } from 'react';
import { facultyApi } from '../api/facultyApi';
import { parseApiError } from '../lib/apiErrors';

export const useFaculty = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFacultyList = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await facultyApi.getAll(params);
      return response.data;
    } catch (err) {
      setError(parseApiError(err, 'Failed to fetch faculty list').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFacultyById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await facultyApi.getById(id);
      return response.data;
    } catch (err) {
      setError(parseApiError(err, 'Failed to fetch faculty').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createFaculty = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await facultyApi.create(data);
      return response.data;
    } catch (err) {
      setError(parseApiError(err, 'Failed to create faculty').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createFacultyAsAdmin = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await facultyApi.createAsAdmin(data);
      return response.data;
    } catch (err) {
      setError(parseApiError(err, 'Failed to create faculty').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateFaculty = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await facultyApi.update(id, data);
      return response.data;
    } catch (err) {
      setError(parseApiError(err, 'Failed to update faculty').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deactivateFaculty = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await facultyApi.deactivate(id);
      return response.data;
    } catch (err) {
      setError(parseApiError(err, 'Failed to deactivate faculty').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const addAvailability = useCallback(async (facultyId, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await facultyApi.addAvailability(facultyId, data);
      return response.data;
    } catch (err) {
      setError(parseApiError(err, 'Failed to add availability').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAvailability = useCallback(async (availabilityId) => {
    setLoading(true);
    setError(null);
    try {
      await facultyApi.deleteAvailability(availabilityId);
    } catch (err) {
      setError(parseApiError(err, 'Failed to delete availability').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  const deleteFaculty = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await facultyApi.delete(id);
      return true;
    } catch (err) {
      setError(parseApiError(err, 'Failed to delete faculty').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reactivateFaculty = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await facultyApi.reactivate(id);
      return response.data;
    } catch (err) {
      setError(parseApiError(err, 'Failed to reactivate faculty').message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    fetchFacultyList,
    fetchFacultyById,
    createFaculty,
    createFacultyAsAdmin,
    updateFaculty,
    deactivateFaculty,
    deleteFaculty,
    reactivateFaculty,
    addAvailability,
    deleteAvailability,
  };
};
